import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  SelectChangeEvent,
} from "@mui/material";
import { IUserFormProps } from "../../interfaces/admin/IUserForm";
import { postDataFromApi, putDataFromApi } from "../../services/api/apiService";
import { RestfulUrls } from "../../constants/restfulUrls";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ADMIN } from "../../constants/admin";
import { useDispatch } from "react-redux";
import { COMMON } from "../../constants/common";

interface UserFormProps extends IUserFormProps {
  onRefresh: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ handleDialogClose, onRefresh }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedUserData = useSelector(
    (state: any) => state.userManagement?.selectedUserData
  );
  const userFormType = useSelector(
    (state: any) => state.userManagement?.userFormType || ADMIN.MEETING_FORM_TYPE.CREATE
  );

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    pbacAccessAllowed: false,
  });

  const [formErrors, setFormErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
  });

  const [formLoading, setFormLoading] = useState(false);

  const userRoles = [
    { value: "ADMIN", label: "Admin" },
    { value: "SUPER_ADMIN", label: "Super Admin" },
    { value: "RAPPORTEUR", label: "Rapporteur" },
    { value: "VIEWER", label: "Viewer" },
  ];

  useEffect(() => {
    if (userFormType === ADMIN.MEETING_FORM_TYPE.EDIT && selectedUserData) {
      setFormData({
        firstName: selectedUserData.firstName || "",
        lastName: selectedUserData.lastName || "",
        email: selectedUserData.email || "",
        role: selectedUserData.role || "",
        pbacAccessAllowed: selectedUserData.pbacAccessAllowed !== undefined ? selectedUserData.pbacAccessAllowed : false,
      });
    } else {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        role: "",
        pbacAccessAllowed: false,
      });
    }
    // Reset errors when form type or selected user changes
    setFormErrors({
      firstName: "",
      lastName: "",
      email: "",
      role: "",
    });
    setFormLoading(false);
  }, [userFormType, selectedUserData]);

  const validateForm = (): boolean => {
    const errors = {
      firstName: "",
      lastName: "",
      email: "",
      role: "",
    };

    let isValid = true;

    // Regex to validate names: only letters, spaces, hyphens, and apostrophes allowed
    // Supports names like "Mary-Jane", "O'Brien", "José", "François"
    const nameRegex = /^[a-zA-ZÀ-ÿ\s'-]+$/;

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
      isValid = false;
    } else if (/\d/.test(formData.firstName)) {
      errors.firstName = "First name cannot contain numbers";
      isValid = false;
    } else if (!nameRegex.test(formData.firstName.trim())) {
      errors.firstName = "First name can only contain letters, spaces, hyphens, and apostrophes";
      isValid = false;
    } else if (formData.firstName.trim().length < 1) {
      errors.firstName = "First name must be at least 1 characters";
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
      isValid = false;
    } else if (/\d/.test(formData.lastName)) {
      errors.lastName = "Last name cannot contain numbers";
      isValid = false;
    } else if (!nameRegex.test(formData.lastName.trim())) {
      errors.lastName = "Last name can only contain letters, spaces, hyphens, and apostrophes";
      isValid = false;
    } else if (formData.lastName.trim().length < 1) {
      errors.lastName = "Last name must be at least 1 characters";
      isValid = false;
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
      isValid = false;
    } else {
      // More comprehensive email validation
      const email = formData.email.trim();
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      
      if (!email.includes("@")) {
        errors.email = "Email must contain '@' symbol";
        isValid = false;
      } else if (!emailRegex.test(email)) {
        // Provide specific error messages
        const [localPart, domain] = email.split("@");
        
        if (!localPart || localPart.length === 0) {
          errors.email = "Email must have a username before '@'";
        } else if (!domain || domain.length === 0) {
          errors.email = "Email must have a domain after '@'";
        } else if (!domain.includes(".")) {
          errors.email = "Email domain must include a '.' (e.g., gmail.com)";
        } else {
          const domainParts = domain.split(".");
          const extension = domainParts[domainParts.length - 1];
          if (extension.length < 2) {
            errors.email = "Email domain extension must be at least 2 characters (e.g., .com, .org)";
          } else {
            errors.email = "Please enter a valid email address";
          }
        }
        isValid = false;
      }
    }

    if (!formData.role) {
      errors.role = "User role is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    handleChange(name, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormLoading(true);

    try {
      if (userFormType === ADMIN.MEETING_FORM_TYPE.EDIT) {
        // Update user firstName, lastName, role, and pbacAccessAllowed
        const url = `${RestfulUrls.BASE_URL}${RestfulUrls.UPDATE_USER_ROLE}${selectedUserData.id}`;
        const payload = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          role: formData.role,
          pbacAccessAllowed: formData.pbacAccessAllowed,
        };

        console.log("Updating user with payload:", payload);
        console.log("URL:", url);

        await putDataFromApi<any>(
          url,
          payload,
          (response) => {
            console.log("User update response:", response);
            setFormLoading(false);
            dispatch({
              type: COMMON.SHOW_SNACKBAR,
              payload: {
                message: "User updated successfully.",
                severity: COMMON.SNACKBAR_SEVERITY.SUCCESS,
              },
            });
            handleDialogClose();
            onRefresh();
          },
          { isAuthRequired: true },
          navigate,
          (error: any) => {
            setFormLoading(false);
            console.error("Error updating user:", error);
            console.error("Error details:", error?.response?.data);
          }
        );
      } else {
        // Create new user
        const url = `${RestfulUrls.BASE_URL}${RestfulUrls.CREATE_USER}`;
        const payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: formData.role,
          pbacAccessAllowed: formData.pbacAccessAllowed,
        };

        await postDataFromApi<any>(
          url,
          payload,
          () => {
            setFormLoading(false);
            // Reset form data
            setFormData({
              firstName: "",
              lastName: "",
              email: "",
              role: "",
              pbacAccessAllowed: false,
            });
            setFormErrors({
              firstName: "",
              lastName: "",
              email: "",
              role: "",
            });
            dispatch({
              type: COMMON.SHOW_SNACKBAR,
              payload: {
                message: "User created successfully.",
                severity: COMMON.SNACKBAR_SEVERITY.SUCCESS,
              },
            });
            handleDialogClose();
            onRefresh();
          },
          { isAuthRequired: true },
          navigate,
          (error: any) => {
            setFormLoading(false);
            // Error is already handled by apiService, but we can add additional handling here if needed
            console.error("Error creating user:", error);
          }
        );
      }
    } catch (error: any) {
      console.error("User operation failed", error);
      setFormLoading(false);
      dispatch({
        type: COMMON.SHOW_SNACKBAR,
        payload: {
          message: error?.response?.data?.detail || "Operation failed. Please try again.",
          severity: COMMON.SNACKBAR_SEVERITY.ERROR,
        },
      });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            error={!!formErrors.email}
            helperText={formErrors.email}
            disabled={userFormType === ADMIN.MEETING_FORM_TYPE.EDIT}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!formErrors.role}>
            <InputLabel>User Role</InputLabel>
            <Select
              name="role"
              value={formData.role}
              onChange={handleSelectChange}
              label="User Role"
            >
              {userRoles.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
            {formErrors.role && (
              <Box sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5, ml: 1.75 }}>
                {formErrors.role}
              </Box>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.pbacAccessAllowed}
                onChange={(e) => handleChange("pbacAccessAllowed", e.target.checked)}
                name="pbacAccessAllowed"
              />
            }
            label="PBAC Access Allowed"
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleDialogClose}
              disabled={formLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formLoading}
            >
              {formLoading
                ? "Saving..."
                : userFormType === ADMIN.MEETING_FORM_TYPE.EDIT
                ? "Update"
                : "Create"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserForm;

